import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { nodesToPdf } from '../utils/pdf';
import '../styles/Reports.scss';
import SubMinWarning from '../components/SubMinWarning';
import api from '../services/api';
import { chartColors, themeVars } from '../config/theme';

const CHART = chartColors('procurement');

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
const fmtCurrency = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PPReportsChart = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const downloadPDF = async () => {
    const hide = ['.reports-download-btn', '.reports-back'].map((sel) => document.querySelector(sel)).filter(Boolean);
    await nodesToPdf(reportRef.current, 'preferential-procurement-report.pdf', {
      beforeCapture: () => hide.forEach((n) => { n.style.visibility = 'hidden'; }),
      afterCapture: () => hide.forEach((n) => { n.style.visibility = 'visible'; }),
    });
  };
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [ppSpendSummary, setppSpendSummary] = useState (null);
  const [loading, setLoading] = useState(true);
  const [tmps, setTMPS] = useState(null);
  const [indicators, setIndicators] = useState([]);

  useEffect(() => {
    api.get('/core/scorecard/get_scorecard/?element=procurement')
      .then((res) => {
        setResults(res.data.results);
        setTotalPoints(res.data.total_points);
        setppSpendSummary(res.data.pp_spend_summary || null);
        setTMPS(res.data.tmps || null);
        setLoading(false);
        setIndicators(res.data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend    
      })
      .catch(() => setLoading(false));
  }, []);

  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};


  const chartData = {
    labels: indicators.map((ind) =>
      ind.label.length > 35 ? ind.label.slice(0, 35) + '…' : ind.label
    ),
    datasets: [
      {
        label: 'Points Achieved',
        data: indicators.map((ind) => resultMap[ind.id]?.points ?? 0),
        backgroundColor: CHART.fill,
        borderColor: CHART.border,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Max Points',
        data: indicators.map((ind) => ind.points),
        backgroundColor: CHART.track,
        borderColor: CHART.trackBorder,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          title: (items) => indicators[items[0].dataIndex].label,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
        title: { display: true, text: 'Points' },
      },
      x: {
        ticks: { font: { size: 11 } },
      },
    },
  };

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  return (
    <div className="reports-container themed" style={themeVars('procurement')} ref={reportRef}>
      <div className="reports-header">
        <button className="reports-back" onClick={() => navigate('/')}>← Back</button>
        <div>
          <h1>Reports</h1>
          <p>B-BBEE — Preferential Procurement Performance Overview</p>
        </div>
        <button className="reports-back reports-download-btn" style={{ marginLeft: 'auto' }} onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      <div className="reports-body">
      {loading && <p className="reports-loading">Loading scorecard data…</p>}

      {!loading && !results && (
        <div className="reports-empty">
          No scorecard data found. Go to the{' '}
          <button
            className="reports-inline-link"
            onClick={() => navigate('/preferential-procurement/scorecard')}
          >
            Preferential Procurement Scorecard
          </button>{' '}
          to calculate your results first.
        </div>
      )}

      {!loading && results && (
        <>
          <div className="reports-summary">
            <div className="reports-summary-card reports-summary-card--primary">
              <div className="reports-summary-head">
                <span className="reports-summary-label">Total Preferential Points</span>
              </div>
              <div className="reports-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
              <div className="reports-summary-ctx">
                <span>Full score <b>{maxPoints || '—'}</b></span>
                <span>Gap <b>{(maxPoints - totalPoints).toFixed(2)}</b></span>
              </div>
            </div>

            {(() => {
              const pct = Math.round((totalPoints / maxPoints || '—') * 100);
              const offset = 264 - (264 * Math.min(pct, 100) / 100);
              return (
                <div className="reports-summary-card">
                  <div className="reports-summary-head">
                    <span className="reports-summary-label">Achievement</span>
                    <span className={`reports-summary-risk${pct >= 60 ? ' reports-summary-risk--ok' : ''}`}>
                      <i></i>{pct >= 60 ? 'On Track' : 'At Risk'}
                    </span>
                  </div>
                  <div className="reports-summary-gauge">
                    <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet">
                      <defs>
                        <linearGradient id="gaugeGradPP" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={CHART.fill} />
                          <stop offset="100%" stopColor={CHART.fill} />
                        </linearGradient>
                      </defs>
                      <path className="gauge-track" d="M 16 100 A 84 84 0 0 1 184 100" />
                      <path className="gauge-fill" d="M 16 100 A 84 84 0 0 1 184 100"
                        stroke="url(#gaugeGradPP)"
                        strokeDasharray="264"
                        strokeDashoffset={offset} />
                    </svg>
                    <div className="reports-summary-gauge-label">
                      <span className="v">{pct}<span className="pct">%</span></span>
                      <small>vs 100% full score</small>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="reports-summary-card">
              <div className="reports-summary-head">
                <span className="reports-summary-label">Indicators Fully Achieved</span>
              </div>
              <div className="reports-summary-score">
                {results.filter((r) => {
                  const ind = indicators.find((i) => i.id === r.id);
                  return ind && r.points >= ind.points;
                }).length}
                <span> / {indicators.length}</span>
              </div>
            </div>
          </div>

    {ppSpendSummary && ppSpendSummary.length > 0 && (
        <div className="sc-table-outer">
          <h2 className="sc-spend-title">Spend Breakdown by Category</h2>
          <div className="sc-table-wrapper">
            <table className="sc-table sc-spend-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Actual Spend</th>
                  <th>Target Spend</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {tmps != null && (
                  <tr className="sc-tmps-row">
                    <td><strong>Total Measured Procurement Spend (TMPS)</strong></td>
                    <td colSpan="3">{fmtCurrency(tmps)}</td>
                  </tr>
                )}
                {ppSpendSummary.map((row) => (
                  <tr key={row.category} className={row.spend_diff > 0 ? 'sc-row--achieved' : 'sc-row--partial'}>
                    <td>{row.category}</td>
                    <td>{row.spend != null ? fmtCurrency(row.spend) : '—'}</td>
                    <td>{row.target_spend != null ? fmtCurrency(row.target_spend) : '—'}</td>
                    <td>
                      {row.spend_diff != null ? (
                        <span className={row.spend_diff > 0 ? 'sc-spend-diff sc-spend-diff--pos' : 'sc-spend-diff sc-spend-diff--neg'}>
                          {row.spend_diff > 0 ? '+' : ''}{fmtCurrency(row.spend_diff)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

          <SubMinWarning results={results} />

          <div className="reports-chart-card">
            <h2 className="reports-chart-heading">Preferential Procurement — Points Achieved vs Maximum</h2>
            <p className="reports-chart-sub">Per indicator breakdown across all Procurement Requirements</p>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default PPReportsChart;
